/**
 * Builder helpers — phase 2 (decisions 59, 60, 61, 63).
 *
 * The freeze/refresh logic lives here, in shared code, so it is unit-tested and
 * used identically by the API. ⚠️ Freeze-at-creation is the rule that keeps
 * quotes honest: a line copies price, discount context, cost basis AND
 * description (decision 60) from the catalogue the moment it is added, and
 * nothing recomputes from live data afterwards. "Refresh prices" is the ONE
 * deliberate exception, for drafts only, and it re-freezes — it never links a
 * line back to the live catalogue.
 */

import { round2 } from './money.js'
import { checkSellPrice } from './pricing.js'
import { lineKeysFor } from './productLines.js'
import type { PartnerMode, PricedProduct, BillingFrequency } from './types.js'

// ---------------------------------------------------------------------------
// Quote titles carry the site name + postcode (decision 61, reworked in
// decision 153 from a "… — {postcode}" SUFFIX to a "{Site Name}, {Postcode} : "
// PREFIX at Mark's UAT request — the identifying facts read first, the rest
// (often a template name) follows).
// ---------------------------------------------------------------------------

const PC = '[A-Za-z]{1,2}\\d[A-Za-z\\d]?\\s*\\d[A-Za-z]{2}'
/** The LEGACY trailing " — ST18 0LP" / " — Multi-site" suffix (decision 61). Still
 *  stripped so old drafts migrate cleanly, and still used by the renewals seed. */
const TITLE_SUFFIX = new RegExp(`\\s+—\\s+(?:${PC}|Multi-site)\\s*$`)
/** The new leading "{name}, {postcode} : " / "{name}, Multi-site : " prefix — with
 *  or without a trailing " : rest", so re-applying never stacks it. */
const TITLE_PREFIX = new RegExp(`^.*?(?:${PC}|Multi-site)(?:\\s*:\\s+|\\s*$)`, 'i')

/** The LEGACY suffix a title should carry for these sites (kept for the renewals seed). */
export function titleSuffixFor(sites: Array<{ postcode?: string | null }>): string | null {
  if (sites.length === 0) return null
  if (sites.length > 1) return 'Multi-site'
  const pc = sites[0].postcode?.trim().toUpperCase()
  return pc || null
}

/** Strip whatever standard a title already carries — legacy suffix FIRST (so a
 *  real base like "Trust rollout" survives "Trust rollout — Multi-site"), then
 *  the new prefix. "New quote" is the placeholder title, treated as empty. */
function stripTitleStandard(title: string): string {
  const base = title.replace(TITLE_SUFFIX, '').replace(TITLE_PREFIX, '').trim()
  return /^new quote$/i.test(base) ? '' : base
}

/** "{Site Name}, {Postcode}" for one site, "{Customer}, Multi-site" for several,
 *  or just the parts that exist. null = no sites yet, leave the title alone. */
export function titlePrefixFor(
  sites: Array<{ name?: string | null; postcode?: string | null }>,
  customerName?: string | null,
): string | null {
  if (sites.length === 0) return null
  const parts = sites.length > 1
    ? [customerName?.trim(), 'Multi-site']
    : [sites[0].name?.trim(), sites[0].postcode?.trim().toUpperCase()]
  const joined = parts.filter(Boolean).join(', ')
  return joined || null
}

/**
 * Prefill-and-maintain "{Site Name}, {Postcode} : {rest}" (decision 153). Strips
 * any standard the title already carries before re-applying, so changing site
 * never stacks. With no sites the title is returned untouched; when the only
 * content is the placeholder, the prefix stands alone.
 */
export function applyTitleStandard(
  title: string,
  sites: Array<{ name?: string | null; postcode?: string | null }>,
  customerName?: string | null,
): string {
  const prefix = titlePrefixFor(sites, customerName)
  const base = stripTitleStandard(title)
  if (!prefix) return base
  return base ? `${prefix} : ${base}` : prefix
}

// ---------------------------------------------------------------------------
// Freeze at creation (spec §5, decision 60)
// ---------------------------------------------------------------------------

/** The frozen slice of a quote item, copied from the catalogue at add time. */
export interface FrozenLine {
  hsProductId: string
  name: string
  /** Customer-facing quote copy (decision 60) — frozen, then editable per quote. */
  description: string | null
  listPrice: number
  buyPrice: number
  sellPrice: number
  recurring: boolean
  billingFrequency: BillingFrequency
  termMonths: number | null
}

/**
 * Copy everything a quote line needs out of a priced catalogue product.
 * `buyPrice` already encodes the mode's cost basis (tier-discounted buy for
 * channel, Wave9 unit cost for internal — decision 53), so the frozen line
 * carries price, discount AND cost in three numbers.
 */
export function freezeLine(product: PricedProduct): FrozenLine {
  return {
    hsProductId: product.hsProductId,
    name: product.name,
    description: product.description ?? null,
    listPrice: round2(product.listPrice),
    buyPrice: round2(product.buyPrice),
    sellPrice: round2(product.defaultSellPrice),
    recurring: product.recurring,
    billingFrequency: product.billingFrequency ?? (product.recurring ? 'annually' : 'one_time'),
    termMonths: product.termMonths ?? null,
  }
}

// ---------------------------------------------------------------------------
// Draft staleness + refresh (spec §6.1 step 5, decision 60c)
// ---------------------------------------------------------------------------

export interface StaleCheck {
  stale: boolean
  priceMoved: boolean
  descriptionMoved: boolean
}

/**
 * Has the catalogue moved under this frozen line? Compares what refresh would
 * change: list, buy, and the description (decision 60 — "refresh prices" must
 * refresh descriptions too, or drafts pin stale copy).
 *
 * ⚠️ A line whose description was hand-edited in the builder is deliberately
 * still flagged when the CATALOGUE copy changes — the flag says "the catalogue
 * moved", and the refresh decision (including losing an edit) is the user's.
 */
export function checkStale(
  frozen: Pick<FrozenLine, 'listPrice' | 'buyPrice' | 'description'>,
  current: Pick<PricedProduct, 'listPrice' | 'buyPrice' | 'description'>,
): StaleCheck {
  const priceMoved =
    round2(frozen.listPrice) !== round2(current.listPrice) ||
    round2(frozen.buyPrice) !== round2(current.buyPrice)
  const descriptionMoved = (frozen.description ?? null) !== (current.description ?? null)
  return { stale: priceMoved || descriptionMoved, priceMoved, descriptionMoved }
}

/**
 * Re-freeze a draft line from the current catalogue (the one deliberate
 * exception to freeze-at-creation; drafts only, sent quotes never reprice).
 *
 * The user's sell price survives where the mode allows it:
 *   agent    -> re-locked to the NEW list
 *   reseller -> kept, but lifted to the new floor if the new buy rose above it
 *   internal -> kept as typed (no floor, decision 27)
 */
export function refreshLine(
  mode: PartnerMode,
  frozenSell: number,
  current: PricedProduct,
): FrozenLine {
  const fresh = freezeLine(current)
  if (mode === 'agent') return fresh // sell = new list, locked
  const check = checkSellPrice(mode, {
    listPrice: fresh.listPrice,
    buyPrice: fresh.buyPrice,
    sellPrice: frozenSell,
  })
  return { ...fresh, sellPrice: check.ok ? round2(frozenSell) : check.corrected }
}

// ---------------------------------------------------------------------------
// ECC notice (decision 59) — offered when the quote carries fibre/broadband
// ---------------------------------------------------------------------------

/** Product Lines whose lines make "excess construction charges" a real possibility. */
export const SURVEYABLE_LINES = ['Fibre Circuit', 'Broadband'] as const

export function hasSurveyableLines(categories: Array<string | null | undefined>): boolean {
  return categories.some((c) =>
    lineKeysFor({ category: c ?? null }).some((k) =>
      (SURVEYABLE_LINES as readonly string[]).includes(k)),
  )
}

// ---------------------------------------------------------------------------
// T&Cs suggestion (decision 63)
// ---------------------------------------------------------------------------

export interface TermsSetLite {
  id: string
  name: string
  /** Product Line names that make this set the suggested one. */
  suggestLines: string[]
}

/**
 * The set the send step should suggest, from the Product Lines actually on the
 * quote. First match in library sort order wins; null = no suggestion (the
 * partner-mode default stands).
 */
export function suggestTermsSet(
  sets: TermsSetLite[],
  categories: Array<string | null | undefined>,
): TermsSetLite | null {
  const keys = new Set(categories.flatMap((c) => lineKeysFor({ category: c ?? null })))
  return sets.find((s) => s.suggestLines.some((l) => keys.has(l))) ?? null
}

// ---------------------------------------------------------------------------
// Recipients (decision 25 — up to ~3, each with their own tracked link)
// ---------------------------------------------------------------------------

export const MAX_RECIPIENTS = 3
